/**
 * CareerAI（ヤギコンシェルジュ）の送信と求人カード取得。
 *
 * CareerAI.vue から切り出した（SFC の行数上限に収めるための分割）。
 * ストリーミング応答の組み立てと、モード切替の自動再送を含む。
 *
 * ホストのインスタンスを丸ごと受け取らず、**必要な ref と関数だけ**を受け取る
 * （暗黙の書き込み結合を作らない / `<script setup>` に proxy が無いため）。
 */
import type { Ref } from 'vue'

import careerAIClient from '@/services/career-ai-client'

/* eslint-disable @typescript-eslint/no-explicit-any */

export type CareerAiSendContext = {
  getUuid: () => string
  getSessionId: () => string
  messages: Ref<any[]>
  jobsCache: Ref<Record<string, any>>
  currentMode: Ref<string>
  // ユーザーが＋メニューで明示的に選んだモード。空なら未指定。
  // 入っている間はサーバー応答（mode / suggest_mode）でモードを変えない
  lockedMode: Ref<string>
  // 画面が先に出したボット発言のモード。サーバーは履歴を DB から作るので、
  // 次の送信でこれを渡して履歴へ入れてもらう（本文はサーバーが持つ。
  // 任意文を渡せると assistant 発言を捏造できてしまう）
  kickoffMode: Ref<string>
  input: Ref<string>
  isComposing: Ref<boolean>
  loading: Ref<boolean>
  sessionRemaining: Ref<number>
  rateLimitIsPrime: Ref<boolean | null>
  validModes: string[]
  scrollToBottom: () => void
  trackEvent: (eventName: string, params?: any) => void
}

export function useCareerAiSend(ctx: CareerAiSendContext) {
  const VALID_MODES = ctx.validModes

  // 新しいキーの追加は差し替えで行う（Vue 2 の $set を増やさない）
  const setJob = (id: string, job: any): void => {
    ctx.jobsCache.value = { ...ctx.jobsCache.value, [id]: job }
  }
  const setMessageField = (index: number, key: string, value: any): void => {
    const msg = ctx.messages.value[index]
    if (msg) ctx.messages.value.splice(index, 1, { ...msg, [key]: value })
  }

  const fetchJobsDetails = async (jobIds: any[]) => {
    if (!ctx.getUuid() || !jobIds || jobIds.length === 0) return
    const tasks = jobIds.map(async (id: any) => {
      if (ctx.jobsCache.value[id]) return
      try {
        const res = await careerAIClient.getJobDetail(ctx.getUuid(), id)
        if (res && res.success && res.data) {
          setJob(id, Object.assign({}, res.data, { id }))
        } else {
          setJob(id, {
            id,
            disabled: true,
            title: '（この求人は取得できませんでした）'
          })
        }
      } catch (_) {
        setJob(id, {
          id,
          disabled: true,
          title: '（この求人は取得できませんでした）'
        })
      }
    })
    await Promise.all(tasks)
  }

  const sendMessage = async (overrideText?: any) => {
    if (ctx.isComposing.value) return
    const rawText =
      typeof overrideText === 'string' ? overrideText : ctx.input.value
    const text = (rawText || '').trim()
    if (!text || ctx.loading.value) return
    if (ctx.sessionRemaining.value <= 0) return
    if (!ctx.getUuid() || !ctx.getSessionId()) {
      ctx.messages.value.push({
        role: 'assistant',
        text: 'セッションが開始されていません。一度閉じてやり直してください。',
        jobIds: [],
        suggestMode: null,
        modeSwitchDismissed: false
      })
      return
    }

    ctx.input.value = ''
    // 送信が受け付けられるまで消さない。先に消すと通信失敗時に
    // 文脈が失われたまま再送されてしまう（meta 受信で消す）
    const kickoffToSend = ctx.kickoffMode.value
    ctx.trackEvent('career_ai_message_sent', {
      mode: ctx.currentMode.value,
      remaining: ctx.sessionRemaining.value
    })
    ctx.messages.value.push({
      role: 'user',
      text,
      jobIds: [],
      suggestMode: null,
      modeSwitchDismissed: false
    })
    ctx.scrollToBottom()

    // streaming 用の assistant プレースホルダ
    ctx.messages.value.push({
      role: 'assistant',
      text: '',
      jobIds: [],
      suggestMode: null,
      modeSwitchDismissed: false
    })
    const assistantIndex = ctx.messages.value.length - 1
    ctx.loading.value = true

    const updateAssistant = (patch: any) => {
      const cur = ctx.messages.value[assistantIndex] || {}
      ctx.messages.value.splice(
        assistantIndex,
        1,
        Object.assign({}, cur, patch)
      )
    }

    let activeAssistantIndex = assistantIndex
    const updateActive = (patch: any) => {
      const cur = ctx.messages.value[activeAssistantIndex] || {}
      ctx.messages.value.splice(
        activeAssistantIndex,
        1,
        Object.assign({}, cur, patch)
      )
    }
    const handlers = {
      onMeta: (data: any) => {
        // サーバーが受け付けた。以降は履歴に入っているので消す
        if (kickoffToSend) ctx.kickoffMode.value = ''
        if (data && typeof data.remaining === 'number') {
          ctx.sessionRemaining.value = data.remaining
        }
      },
      onThinking: () => {},
      onText: (data: any) => {
        if (data && typeof data.content === 'string') {
          const cur = ctx.messages.value[activeAssistantIndex] || {}
          // placeholder 状態だったら最初の text で上書き、それ以降は累積
          if (cur.isPlaceholder) {
            updateActive({ text: data.content, isPlaceholder: false })
          } else {
            updateActive({ text: (cur.text || '') + data.content })
          }
          ctx.scrollToBottom()
        }
      },
      onAssistantBreak: (data: any) => {
        // サーバが第2ターンに切り替わるシグナル: 新規 assistant メッセージを開始する
        // placeholder があれば薄いテキストで「検索中...」表示
        const placeholder = (data && data.placeholder) || ''
        ctx.messages.value.push({
          role: 'assistant',
          text: placeholder,
          isPlaceholder: !!placeholder,
          jobIds: [],
          suggestMode: null,
          modeSwitchDismissed: false
        })
        activeAssistantIndex = ctx.messages.value.length - 1
        ctx.scrollToBottom()
      },
      onComplete: async (data: any) => {
        // [DEBUG] サーバ応答内容を確認するための一時ログ
        if (typeof console !== 'undefined' && console.log) {
          console.log(
            '[career_ai] onComplete data:',
            JSON.stringify({
              mode: data && data.mode,
              suggest_mode: data && data.suggest_mode,
              job_ids: data && data.job_ids,
              body_len: data && data.body ? data.body.length : 0
            })
          )
        }
        if (data) {
          const safeSuggestMode =
            data.suggest_mode && VALID_MODES.indexOf(data.suggest_mode) >= 0
              ? data.suggest_mode
              : null
          // job_ids は最新のアクティブメッセージ（第2ターン分）に適用
          const incomingJobIds = Array.isArray(data.job_ids) ? data.job_ids : []
          const currentMsg = ctx.messages.value[activeAssistantIndex] || {}
          const keptJobIds =
            incomingJobIds.length > 0
              ? incomingJobIds
              : Array.isArray(currentMsg.jobIds)
                ? currentMsg.jobIds
                : []
          updateActive({
            jobIds: keptJobIds,
            suggestMode: safeSuggestMode,
            modeSwitchDismissed: true
          })
          // ユーザーが機能を選んでいる間は、サーバー主導のモード変更を無視する。
          // 面接練習の途中で inquiry や career へ流れると練習が中断されるため
          const modeLocked = !!ctx.lockedMode.value
          if (!modeLocked && data.mode && VALID_MODES.indexOf(data.mode) >= 0) {
            ctx.currentMode.value = data.mode
          }
          if (data.job_ids && data.job_ids.length > 0) {
            await fetchJobsDetails(data.job_ids)
          }
          ctx.scrollToBottom()
          // suggest_mode が現モードと異なる場合の挙動分岐
          //  - 既に求人提示が伴っている応答（job_ids あり）→ モードだけ静かに更新（再送しない、画面に求人カードを残す）
          //  - 求人提示なしの提案応答 → 直前ユーザー質問を新モードで再送
          if (
            !modeLocked &&
            safeSuggestMode &&
            safeSuggestMode !== ctx.currentMode.value
          ) {
            const hasJobs =
              Array.isArray(data.job_ids) && data.job_ids.length > 0
            if (hasJobs) {
              ctx.currentMode.value = safeSuggestMode
            } else {
              confirmMode(assistantIndex, safeSuggestMode)
            }
          }
        }
      },
      onDone: (data: any) => {
        if (data && typeof data.remaining === 'number') {
          ctx.sessionRemaining.value = data.remaining
        }
        ctx.loading.value = false
      },
      onError: (data: any) => {
        ctx.loading.value = false
        // 失敗した往復を正常応答と区別する（全画面案内の回数に数えない）。
        // assistant_break で分かれた末尾のバブルにも印を付ける
        updateAssistant({
          isError: true,
          text:
            (data && data.message) ||
            '通信エラーが発生しました。もう一度お試しください。'
        })
        if (activeAssistantIndex !== assistantIndex) {
          updateActive({ isError: true })
        }
        if (data && typeof data.remaining === 'number') {
          // サーバ側でレート予約が巻き戻された後の残数（エラー時は消費しない）
          ctx.sessionRemaining.value = data.remaining
        }
        if (data && data.code === 'RATE_LIMIT') {
          ctx.sessionRemaining.value = 0
          ctx.rateLimitIsPrime.value = data.is_prime === true
          ctx.trackEvent('career_ai_rate_limit_hit', {
            is_prime: ctx.rateLimitIsPrime.value
          })
        }
        ctx.scrollToBottom()
      }
    }

    try {
      await careerAIClient.streamChat(
        {
          uuid: ctx.getUuid(),
          session_id: ctx.getSessionId(),
          message: text,
          current_mode: ctx.currentMode.value,
          // ユーザーが機能を選んでいる間はモデルにモードを変えさせない
          mode_locked: !!ctx.lockedMode.value,
          kickoff_mode: kickoffToSend || ''
        },
        handlers
      )
    } catch (_) {
      ctx.loading.value = false
      updateAssistant({
        isError: true,
        text: '通信エラーが発生しました。もう一度お試しください。'
      })
      if (activeAssistantIndex !== assistantIndex) {
        updateActive({ isError: true })
      }
      ctx.scrollToBottom()
    }
  }

  // サーバーが別モードを提案してきたときの受諾。直前のユーザー質問を
  // 新モードで自動再送する（input は触らず引数経由で送る）
  const confirmMode = (msgIndex: number, mode: string): void => {
    setMessageField(msgIndex, 'modeSwitchDismissed', true)
    if (VALID_MODES.indexOf(mode) < 0) return
    ctx.currentMode.value = mode
    let lastUserText = ''
    for (let j = ctx.messages.value.length - 1; j >= 0; j--) {
      const msg = ctx.messages.value[j]
      if (msg.role === 'user' && msg.text) {
        lastUserText = msg.text
        break
      }
    }
    if (
      lastUserText &&
      ctx.sessionRemaining.value > 0 &&
      ctx.getUuid() &&
      ctx.getSessionId()
    ) {
      sendMessage(lastUserText)
    }
  }

  return { fetchJobsDetails, sendMessage, confirmMode }
}
